<template>
  <div>
    <error-box v-if="error" @reload="load()" />
    <loader v-else-if="loading" />

    <template v-if="!error && !loading && seminar">
      <el-alert
        :type="seminar.registrationOpen ? 'success' : 'error'"
        show-icon
        :closable="false"
        class="mb-20"
      >
        {{
          seminar.registrationOpen
            ? $tc('tournament.applications_are_open')
            : $tc('tournament.applications_are_closed')
        }}
      </el-alert>

      <el-row :gutter="20" style="margin-bottom: 30px">
        <el-col :sm="24" :md="16">
          <seminar-panel
            :seminar="seminar"
            @registration-fee="openRegistrationFee"
            @print-registration-fee="handlePrintRegistrationFee"
            @print-participants="handlePrintParticipants"
            @print-documents="showDocumentPrintModal = true"
          />
          <seminar-applications-panel
            :seminar="seminar"
            :participants="participants"
            :selected-club="user.club"
            :can-edit="canEdit"
            :show-club-name="user.isAdmin"
            :loader="participantsLoader"
            @participants-change="loadParticipants"
            @registration-fee="openRegistrationFee"
            @print-participants="handlePrintParticipants"
          />
          <seminar-history
            v-if="user.isAdmin"
            :seminar="seminar"
            :data="history"
          />
        </el-col>
        <el-col :sm="24" :md="8" class="right-side">
          <panel :title="$tc('general.informations')" :loader="participantsLoader">
            <data-list :list="infoData" />
          </panel>
          <panel
            v-if="user.club"
            :title="$tc('club.my_federation_information')"
            :loader="participantsLoader"
          >
            <data-list :list="myInfoData" />
          </panel>
          <!-- Club discounts section hidden for now; logic kept in SeminarClubCostDeduction -->
          <seminar-club-cost-deduction
            v-if="false"
            :seminar="seminar"
            class="mt-20"
          />
        </el-col>
      </el-row>
    </template>

    <seminar-registration-fee
      v-if="registrationFeeVisible"
      :seminar="seminar"
      :participants="participantsForFee"
      :title="registrationFeeTitle"
      :show-club-name="user.isAdmin"
      @close="registrationFeeVisible = false"
    />

    <div v-show="false" ref="printWrap">
      <print-seminar-registration-fee-by-club
        v-if="seminar"
        ref="printTable"
        :seminar="seminar"
        :participants="participantsForFee"
      />
    </div>

    <div v-show="false" ref="printParticipantsWrap">
      <print-seminar-participants
        v-if="seminar"
        ref="printParticipantsTable"
        :participants="participantsForPrint"
        :show-club-name="user.isAdmin"
      />
    </div>

    <document-print-options-modal
      v-model="showDocumentPrintModal"
      :seminar-id="seminar && seminar.id"
      @print="handleDocumentPrint"
    />
    <div v-show="false" ref="printDocumentsWrap">
      <print-documents
        v-if="currentDocumentType"
        ref="printDocumentsComponent"
        :data="documentPrintData"
        :document-type="currentDocumentType"
        :target="currentDocumentTarget"
        entity-type="seminar"
        :competition-name="seminar && seminar.name"
      />
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import doPrint from '@/common/print'
import escapeHtml from '@/common/escapeHtml.js'
import { getSeminarParticipantsSortBy, seminarParticipantsOrderBy } from '@/common/util'
import ErrorBox from '@/components/other/ErrorBox'
import Panel from '@/components/panel/Panel'
import DataList from '@/components/data-list/DataList'
import SeminarPanel from '@/components/seminars/SeminarPanel'
import SeminarApplicationsPanel from '@/components/seminars/SeminarApplicationsPanel'
import SeminarHistory from '@/components/seminars/SeminarHistory'
import SeminarClubCostDeduction from '@/components/seminars/SeminarClubCostDeduction'
import SeminarRegistrationFee from '@/components/seminars/SeminarRegistrationFee'
import PrintSeminarRegistrationFeeByClub from '@/components/seminars/PrintSeminarRegistrationFeeByClub'
import PrintSeminarParticipants from '@/components/seminars/PrintSeminarParticipants'
import DocumentPrintOptionsModal from '@/components/documents/DocumentPrintOptionsModal.vue'
import PrintDocuments from '@/components/documents/PrintDocuments.vue'

export default {
  components: {
    ErrorBox,
    Panel,
    DataList,
    SeminarPanel,
    SeminarApplicationsPanel,
    SeminarHistory,
    SeminarClubCostDeduction,
    SeminarRegistrationFee,
    PrintSeminarRegistrationFeeByClub,
    PrintSeminarParticipants,
    DocumentPrintOptionsModal,
    PrintDocuments
  },

  data () {
    return {
      error: false,
      loading: false,
      participantsLoader: false,
      historyLoader: false,
      participants: [],
      history: [],
      registrationFeeVisible: false,
      registrationFeeTitle: '',
      showDocumentPrintModal: false,
      currentDocumentType: null,
      currentDocumentTarget: null,
      documentPrintData: []
    }
  },

  computed: {
    seminarId () {
      return this.$route.params.id
    },
    seminar () {
      return this.$store.getters['seminars/get'](this.seminarId)
    },
    user () {
      return this.$store.state.auth.user
    },
    canEdit () {
      return !this.seminar?.isArchived && (this.user.isAdmin || this.seminar?.registrationOpen)
    },
    participantsForFee () {
      if (!this.seminar || !this.participants.length) return []
      if (this.user.isAdmin) return this.participants
      const clubId = this.user?.club?.id
      if (!clubId) return []
      return this.participants.filter(p => parseInt(p.club?.id) === parseInt(clubId))
    },
    participantsForPrint () {
      const sortBy = getSeminarParticipantsSortBy()
      return seminarParticipantsOrderBy(this.participantsForFee, sortBy)
    },
    infoData () {
      const s = this.seminar
      if (!s) return []
      const items = [
        {
          label: this.$tc('tournament.registration_end_date'),
          text: s.endDate ? moment(s.endDate).format('DD.MM.YYYY HH:mm') : '/'
        }
      ]
      if (this.user?.isAdmin) {
        const unitNet = (s.single_fee_1 ?? 0) - (s.single_fee_1_break ?? 0)
        const deductions = s.clubDeductions || []
        const totalFee = this.participants.reduce((sum, p) => {
          const ded = deductions.find(d => parseInt(d.clubId) === parseInt(p.club?.id))?.deduction || 0
          const price = ded ? Math.round((unitNet * (100 - ded)) / 100) : unitNet
          return sum + price
        }, 0)
        const registeredClubs = new Set(
          this.participants.map(p => p.club?.id).filter(id => id != null)
        )
        items.push(
          {
            label: this.$tc('seminar.number_of_registered_participants'),
            text: String(this.participants.length)
          },
          {
            label: this.$tc('tournament.number_of_registered_clubs'),
            text: String(registeredClubs.size)
          },
          {
            label: this.$tc('general.registration_fee'),
            text: this.$formatCurrency(totalFee)
          }
        )
      }
      return items
    },
    myInfoData () {
      const clubId = this.user?.club?.id
      if (!clubId) return []
      const myParticipants = this.participants.filter(p => parseInt(p.club?.id) === parseInt(clubId))
      const unitPrice = (this.seminar?.single_fee_1 ?? 0) - (this.seminar?.single_fee_1_break ?? 0)
      const deduction = (this.seminar?.clubDeductions || []).find(d => parseInt(d.clubId) === parseInt(clubId))?.deduction || 0
      const raw = myParticipants.length * unitPrice
      const total = deduction ? Math.round((raw * (100 - deduction)) / 100) : raw
      return [
        { label: this.$tc('seminar.number_of_registered_participants'), text: String(myParticipants.length) },
        { label: this.$tc('general.registration_fee'), text: this.$formatCurrency(total) }
      ]
    }
  },

  watch: {
    '$route.params.id' () {
      this.load()
    }
  },

  created () {
    this.load()
  },

  methods: {
    async load () {
      this.loading = true
      this.error = false
      try {
        await this.$store.dispatch('seminars/get', this.seminarId)
        await this.loadParticipants()
        await this.loadHistory()
        this.setPageData()
      } catch (e) {
        console.error(e)
        this.error = true
      } finally {
        this.loading = false
      }
    },
    async loadParticipants () {
      this.participantsLoader = true
      try {
        const list = await this.$store.dispatch('seminars/getParticipants', this.seminarId)
        this.participants = list || []
      } catch (e) {
        console.error(e)
        this.participants = []
      } finally {
        this.participantsLoader = false
      }
    },
    async loadHistory () {
      this.historyLoader = true
      try {
        const response = await this.$store.dispatch('seminars/getHistory', this.seminarId)
        this.history = (response || []).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      } catch (e) {
        console.error(e)
        this.history = []
      } finally {
        this.historyLoader = false
      }
    },
    setPageData () {
      this.$store.commit('app/set', {
        title: this.seminar?.name,
        breadcrumbs: [
          { text: this.$tc('seminar.seminars_and_camps'), to: { name: 'Seminars' } },
          { text: this.seminar?.name }
        ]
      })
    },
    openRegistrationFee () {
      this.registrationFeeTitle = this.user.isAdmin ? '' : (this.user?.club?.shortName ? ` - ${this.user.club.shortName}` : '')
      this.registrationFeeVisible = true
    },
    handlePrintRegistrationFee () {
      this.$nextTick(() => {
        const el = this.$refs.printTable?.$el || this.$refs.printWrap
        if (el) doPrint(this.$tc('general.registration_fee'), el)
      })
    },
    handlePrintParticipants () {
      this.$forceUpdate()
      this.$nextTick(() => {
        const el = this.$refs.printParticipantsTable?.$el || this.$refs.printParticipantsWrap
        if (el) doPrint(this.$tc('seminar.applications'), el)
      })
    },

    // Targets emitted by DocumentPrintOptionsModal are always the fixed
    // keys competitors/clubs/coaches/officials/referees (its checkbox for
    // seminars just relabels "competitors" as "Participants" - see
    // competitorsLabel in that component). No placement/bracket variables
    // exist for seminars, so unlike the tournament flow there is no
    // documentTypeUsesPlacementVariables branch here.
    buildItemsForTarget (target) {
      const list = this.participants || []
      if (target === 'competitors') {
        return list.map(p => ({ competitor: p.member, club: p.club, registration: p }))
      }
      if (target === 'clubs') {
        const uniqueClubsMap = new Map()
        list.forEach(p => {
          if (p.club?.id && !uniqueClubsMap.has(parseInt(p.club.id))) {
            uniqueClubsMap.set(parseInt(p.club.id), p.club)
          }
        })
        return Array.from(uniqueClubsMap.values())
          .filter(club => club && (club.name || club.shortName))
          .map(club => ({ competitor: null, club, registration: null }))
      }
      if (target === 'coaches') {
        return list.filter(p => p.coachId).map(p => ({ competitor: p.member, club: p.club, registration: p }))
      }
      if (target === 'officials') {
        return list.filter(p => p.officialId).map(p => ({ competitor: p.member, club: p.club, registration: p }))
      }
      if (target === 'referees') {
        return list.filter(p => p.refereeId).map(p => ({ competitor: p.member, club: p.club, registration: p }))
      }
      return []
    },

    // PrintDocuments renders <canvas> elements; canvas pixel data is not
    // serialized by innerHTML, so grabbing the wrap's raw HTML would print
    // blank pages. Convert each rendered canvas to a PNG <img> first, using
    // the same full-bleed page markup/CSS as printDocumentSections() below
    // (mirrors TournamentPanel's printCertificatesOnly() page structure).
    captureDocumentPrintHtml () {
      const wrap = this.$refs.printDocumentsWrap
      if (!wrap) return ''
      const canvases = wrap.querySelectorAll('canvas')
      let html = ''
      canvases.forEach((canvas, index) => {
        const dataUrl = canvas.toDataURL('image/png', 1.0)
        const breakStyle = index < canvases.length - 1 ? 'page-break-after: always;' : 'page-break-after: auto;'
        html += `<div class="document-page" style="width: 210mm; height: 297mm; ${breakStyle}">`
        html += `<img class="document-page-image" src="${dataUrl}" />`
        html += '</div>'
      })
      return html
    },

    // Seminar document prints need to look the same as tournament document
    // prints: full-bleed, no title, no margins. doPrint()/tableHtml() (used
    // for the other seminar prints below) always render an <h1> title and
    // force 15mm body padding, which would inset/caption these documents -
    // so print them directly in a dedicated window instead, mirroring
    // TournamentPanel's printCertificatesOnly().
    printDocumentSections (title, sections) {
      if (!sections || !sections.length) return

      const documentsHtml = sections.join('')
      const newWin = window.open('')
      const htmlContent = `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <title>${escapeHtml(title)}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              margin: 0;
              padding: 0;
            }
            .document-page {
              margin: 0;
              padding: 0;
              display: flex;
              align-items: stretch;
              justify-content: stretch;
              overflow: hidden;
            }
            .document-page-image {
              width: 100%;
              height: 100%;
              object-fit: contain;
              display: block;
            }
            @media print {
              @page {
                size: A4;
                margin: 0;
              }
              body {
                margin: 0 !important;
                padding: 0 !important;
              }
            }
          </style>
        </head>
        <body>
          ${documentsHtml}
        </body>
        </html>
      `
      newWin.document.write(htmlContent)
      newWin.document.close()

      setTimeout(() => {
        newWin.print()
        newWin.close()
      }, 500)
    },

    // A fixed delay only lets Vue's reactivity settle before we look at the
    // canvases; it doesn't guarantee the (possibly slow) background image
    // has actually finished loading. PrintDocuments exposes a `loading`
    // boolean that stays true until its image's onload/onerror fires - poll
    // it after the fixed delay so a slow-but-eventually-successful load
    // can't get captured while the canvas is still blank. Resolves true
    // once loading has cleared, or false if it's still loading after
    // `extraTimeoutMs` on top of the caller's own delay.
    async waitForDocumentImageReady (extraTimeoutMs = 3000, pollIntervalMs = 100) {
      const start = Date.now()
      while (this.$refs.printDocumentsComponent?.loading) {
        if (Date.now() - start >= extraTimeoutMs) {
          return false
        }
        await new Promise(resolve => setTimeout(resolve, pollIntervalMs))
      }
      return true
    },

    async handleDocumentPrint ({ documentTypeId, targets, manual }) {
      const types = await this.$store.dispatch('seminars/getDocumentTypes', this.seminarId)
      const documentType = types.find(t => t.id === documentTypeId)
      if (!documentType) return
      documentType.entityId = this.seminarId

      // PrintDocuments only fetches its background image in mounted() (no
      // watcher on the documentType prop). Force it to unmount/remount so a
      // second print in the same session re-fetches the correct image
      // instead of rendering the new type's fields onto the previous
      // type's cached one.
      this.currentDocumentType = null
      await this.$nextTick()

      const printSections = []
      let imageLoadFailed = false

      const checkImageError = () => {
        if (this.$refs.printDocumentsComponent?.error) {
          imageLoadFailed = true
        }
        return imageLoadFailed
      }

      if (manual.enabled && manual.rows.length) {
        this.currentDocumentType = documentType
        this.currentDocumentTarget = 'competitors'
        this.documentPrintData = manual.rows.map(row => ({ manualValues: row }))
        await this.$nextTick()
        await new Promise(resolve => setTimeout(resolve, 300))
        const manualImageReady = await this.waitForDocumentImageReady()
        if (!manualImageReady) {
          imageLoadFailed = true
        }
        if (manualImageReady && !checkImageError()) {
          const html = this.captureDocumentPrintHtml()
          if (html) printSections.push(html)
        }
      } else {
        for (const [target, enabled] of Object.entries(targets)) {
          if (!enabled) continue
          if (imageLoadFailed) break
          const items = this.buildItemsForTarget(target)
          if (!items.length) continue
          this.currentDocumentType = documentType
          this.currentDocumentTarget = target
          this.documentPrintData = items
          await this.$nextTick()
          await new Promise(resolve => setTimeout(resolve, 500))
          const imageReady = await this.waitForDocumentImageReady()
          if (!imageReady) {
            imageLoadFailed = true
            break
          }
          if (checkImageError()) break
          const html = this.captureDocumentPrintHtml()
          if (html) printSections.push(html)
        }
      }

      if (imageLoadFailed) {
        this.$message.error(`${this.$tc('tournament.document_image_load_failed') || 'Failed to load the document image for'} "${documentType.name}"`)
      } else if (printSections.length) {
        this.printDocumentSections(documentType.name, printSections)
      }
      this.showDocumentPrintModal = false
    }
  }
}
</script>

<style lang="scss" scoped>
@media (max-width: 991px) {
  .right-side { margin-top: 20px; }
}
</style>
